import Analytics from '@segment/analytics.js-core/build/analytics'; // oxlint-disable-line import/default
import SegmentIntegration from '@segment/analytics.js-integration-segmentio';

import config, {backendHost, envIsServerDeployment} from './config';
import {recordRumError} from './util/datadog/datadogRum';
import {logDedupeSuffix} from './util/ignoredErrorMessages';

interface ChameleonInterface {
  identify: (userId: string | undefined, args: {}) => any;
  track: (trackType: string, metadata: object) => any;
  on: (actionType: string, functionCall: () => void) => any;
  data: any;
}
declare global {
  interface Window {
    chmln?: ChameleonInterface;
  }
}

declare global {
  interface Window {
    // Todo: Figure out how to hook up types from @types/prismjs

    Prism: any;
  }
}

export const Prism = window.Prism;

/** Severity levels the error helpers accept, matching Datadog's log-status vocabulary. */
export enum ErrorLevel {
  Critical = 'critical',
  Error = 'error',
  Warning = 'warn',
  Info = 'info',
  Debug = 'debug',
}

/**
 * Interface representing the parameters for error handling.
 *
 * @property {Object.<string, string>} [tags] - Optional tags to categorize the error.
 * @property {Object.<string, any>} [extra] - Optional additional data to provide more context about the error.
 * @property {ErrorLevel} [level] - Optional severity level of the error.
 * @property {string[]} [fingerprint] - Optional array of strings that identify the
 *  error, used as the Datadog Error Tracking grouping key.
 */
interface ErrorParams {
  tags?: {[key: string]: string};
  extra?: {[key: string]: any};
  level?: ErrorLevel;
  fingerprint?: string[];
}

// You must pass callsite, a string indicating what part of our code
// base captureError was called from.
export function captureError(
  err: Error | string | unknown,
  callsite: string,
  errorParams: ErrorParams = {}
) {
  recordRumError(
    withFingerprint(err, errorParams.fingerprint),
    errorContext(callsite, errorParams)
  );
}

/**
 * Applies a custom grouping key: Datadog Error Tracking groups on
 * `error.fingerprint`, which the browser SDK reads from `dd_fingerprint` on the
 * error passed to `addError`. It only reads that property off an `Error`, so a
 * fingerprinted string is wrapped in one, and it takes a single string, so the
 * array is joined.
 */
function withFingerprint(
  err: Error | string | unknown,
  fingerprint: string[] | undefined
): Error | string | unknown {
  if (fingerprint == null || fingerprint.length === 0) {
    return err;
  }
  const error: Error & {dd_fingerprint?: string} =
    err instanceof Error ? err : new Error(String(err));
  error.dd_fingerprint = fingerprint.join(':');
  return error;
}

/**
 * Flattens the error params for Datadog: RUM context is a single flat object
 * with no separate indexed-tag concept, so tags, extras and level all become
 * `@context.*` attributes. The fingerprint stays in the context too, so it is
 * visible on the console-only paths that never reach Error Tracking.
 */
function errorContext(
  callsite: string,
  {extra, tags, level, fingerprint}: ErrorParams
): {[key: string]: unknown} {
  return {
    ...extra,
    ...tags,
    callsite,
    ...(level != null && {errorLevel: level}),
    ...(fingerprint != null && {errorFingerprint: fingerprint}),
  };
}

/**
 * Logs an error to the console, along with the context that would otherwise be
 * attached to an error report.
 *
 * The dedupe suffix keeps these out of Datadog: RUM reports console errors on
 * its own, and `ignoredErrorMessages` — which RUM's discard filter uses —
 * deliberately drops the ones logged here.
 *
 * @param err - The error to log. Can be an `Error` object, a string, or an unknown type.
 * @param callsite - A string representing the location or context where the error occurred.
 * @param errorParams - Optional additional context to log alongside the error.
 */
export function captureAndLogError(
  err: Error | string | unknown,
  callsite: string,
  errorParams: ErrorParams = {}
) {
  console.error(err, errorContext(callsite, errorParams), logDedupeSuffix);
}

/**
 * Logs an error to the console with its context, and throws it.
 *
 * This function is useful when you want to log an error to the console and then
 * throw it so that it can be caught by a global error handler. As with
 * `captureAndLogError`, the dedupe suffix keeps both the log and the rethrown
 * error out of Datadog.
 *
 * @param err - The error to log and raise. Can be an `Error` object, a string, or an unknown type.
 * @param callsite - A string representing the location or context where the error occurred.
 * @param errorParams - Optional additional context to log alongside the error.
 * @throws An `Error` object with the error message suffixed with a string that is ignored by our error reporting.
 */
export function captureAndThrowError(
  err: Error | string | unknown,
  callsite: string,
  errorParams: ErrorParams = {}
) {
  console.error(err, errorContext(callsite, errorParams), logDedupeSuffix);
  const errorMessage = `${String(err)} ${logDedupeSuffix}`;
  if (err instanceof Error) {
    err.message = errorMessage;
    throw err;
  }
  throw new Error(errorMessage);
}

// Reload on error on dashboard pages
export function shouldReloadOnError(): boolean {
  return window.location.pathname.indexOf('/dashboards') > -1;
}

if (envIsServerDeployment && !config.DISABLE_TELEMETRY) {
  let host = backendHost();
  if (host === '') {
    host = document.location.origin;
  }
  if (host.startsWith('https://')) {
    const apiHost = host.replace('https://', '') + '/analytics';
    const integrationSettings = {
      'Segment.io': {
        apiHost,
        retryQueue: true,
      },
    };
    window.analytics = new (Analytics as any)();
    window?.analytics?.use(SegmentIntegration);
    window?.analytics?.init(integrationSettings);
  }
}
